<template>
  <v-card
    flat
    @keyup.enter.prevent="onPressEnterKey"
  >
    <div>
      <div class="progress-steps">
        <eewc-progress-bar-steps
          :steps="steps"
          :max-width-container="390"
          :value="currentStep"
          @input="changeStep"
        />
      </div>
      <div class="progress-steps__buttons">
        <eewc-button-common
          type="outlined"
          class="progress-steps__cancel-button"
          data-testid="my-profile-change-email-wizard-cancel-btn"
          @click.prevent="router.back()"
        >
          {{ t('Cancel') }}
        </eewc-button-common>
        <eewc-button-common
          v-if="isLastStep"
          class="progress-steps__next-button"
          color="accent"
          :loading="isAuthMailVerifying"
          :disabled="!currentStep.isFilled"
          data-testid="my-profile-change-email-wizard-done-btn"
          @click.prevent="onClickComplete"
        >
          {{ t('Done') }}
        </eewc-button-common>
        <eewc-button-common
          v-else
          class="progress-steps__next-button"
          color="accent"
          :loading="isAuthMailLoading"
          :disabled="!currentStep.isFilled"
          data-testid="my-profile-change-email-wizard-next-btn"
          @click.prevent="onclickNext"
        >
          {{ t('Next') }}
        </eewc-button-common>
      </div>
      <div class="edit-email">
        <v-form
          autocomplete="off"
          @submit.prevent
        >
          <div v-if="currentStep.id === 'reset-email'">
            <div class="edit-email__verify-password">
              <eewc-form-field label="">
                <div class="edit-email__header">
                  <span class="edit-email__header__title">
                    {{ `1. ${t('Verify password')}` }}
                  </span>
                  <span class="edit-email__header__subtitle">
                    {{ t('Please enter your password below.') }}
                  </span>
                </div>
              </eewc-form-field>

              <eewc-form-field :label="t('Password')">
                <eewc-text-field
                  v-model="data.password"
                  data-testid="my-profile-edit-email-wizard-text-field-password"
                  autocomplete="off"
                  :label="t('Enter password')"
                  class="general__inputFields"
                  :type="showPassword ? 'text' : 'password'"
                  :right-icon="showPassword ? '$icon_no_permissions' : '$icon_eye'"
                  :error-messages="passwordErrorMessage"
                  @input="validateFirstStep"
                  @clickAppend="showPassword = !showPassword"
                />
              </eewc-form-field>
            </div>
            <eewc-form-field label="">
              <div class="edit-email__header">
                <span class="edit-email__header__title">
                  {{ `2. ${t('Reset email')}` }}
                </span>
                <span class="edit-email__header__subtitle">
                  {{ t('Please enter the new email address below.') }}
                </span>
              </div>
            </eewc-form-field>
            <eewc-form-field :label="t('Email')">
              <eewc-text-field
                v-model="data.email"
                data-testid="my-profile-edit-email-wizard-text-field-email"
                autocomplete="off"
                class="general__inputFields"
                :label="t('Enter email')"
                type="email"
                :rules="data.email ? emailRules : []"
                @input="validateFirstStep"
              />
            </eewc-form-field>
          </div>
          <div v-if="currentStep.id === 'verify-code'">
            <eewc-form-field>
              <verification-code-step
                v-model="data.code"
                data-testid="my-profile-edit-email-wizard-verification-code"
                :value="data.code"
                :title="t('Verify authentication code')"
                :subtitle="`${t('An authentication code was sent to your email')} ${data.email}. ${t(
                  'Please enter it below.'
                )}`"
                :error-message="codeErrorMessage"
                @input="onCodeInput"
                @resend-code="makeEmailChange(true)"
              />
            </eewc-form-field>
          </div>
        </v-form>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { t } from '@/plugins/i18n.ts';
import { useUsersStore, useMessagingStore } from '@/stores';
import errors from '@/assets/validation/ErrorMessages.json';
import VerificationCodeStep from '@/components/VerificationCodeStep.vue';
import router from '@/service/router';

type EditWizartType = 'reset-email' | 'verify-code';
interface ProgressBarStep {
  id: EditWizartType;
  title: string;
  isCompleted: boolean;
  isFilled?: boolean;
}

const usersStore = useUsersStore();
const messagingStore = useMessagingStore();

const showPassword = ref<boolean>(false);
const emailRules = ref([(value: string) => /.+@.+/.test(value) || t(errors.email)]);
const passwordErrorMessage = ref<string>('');
const codeErrorMessage = ref<string>('');
const isAuthMailLoading = ref<boolean>(false);
const isAuthMailVerifying = ref<boolean>(false);
const isLastStep = computed(() => currentStep.value.id === steps[steps.length - 1].id);

const steps: Array<ProgressBarStep> = [
  {
    id: 'reset-email',
    title: t('Reset email'),
    isCompleted: false,
    isFilled: false,
  },
  {
    id: 'verify-code',
    title: t('Verify code'),
    isCompleted: false,
    isFilled: false,
  },
];

const currentStep = ref(steps[0]);

const onPressEnterKey = () => {
  if (!currentStep.value.isFilled) return;
  if (isLastStep.value) {
    onClickComplete();
  } else {
    onclickNext();
  }
};

const validateFirstStep = () => {
  const isPasswordFilled = data.password.length > 0;
  const isMailValid = data.email && emailRules.value[0](data.email) === true;
  if (isPasswordFilled && isMailValid) {
    steps[0].isFilled = true;
  } else {
    steps[0].isFilled = false;
  }
};

const onCodeInput = (code: string) => {
  codeErrorMessage.value = '';
  currentStep.value.isFilled = code.length === 4;
};

const onClickComplete = async () => {
  isAuthMailVerifying.value = true;
  const status = await usersStore.verifyAuthenticationCode({
    multiFactorAuthentication: {
      code: data.code,
    },
  });
  isAuthMailVerifying.value = false;
  if (status === 204) {
    messagingStore.addNotification({
      iconColor: 'positive',
      icon: '$icon_check_zero',
      title: t('The email has been updated successfully'),
    });
    router.back();
  }
};

const changeStep = (newStep: ProgressBarStep) => {
  if (currentStep.value.id === 'verify-code') {
    steps[0].isCompleted = false;
  }
  currentStep.value = newStep;
};

const onclickNext = async () => {
  if (currentStep.value.id === 'reset-email') {
    return makeEmailChange();
  }
  moveToNextStep();
};

function moveToNextStep() {
  currentStep.value.isCompleted = true;
  currentStep.value = steps[steps.indexOf(currentStep.value) + 1];
}

const makeEmailChange = async (isResent = false) => {
  isAuthMailLoading.value = true;
  const status = await usersStore.updateUserAuthentication({
    login: { emailAddress: data.email, password: data.password },
  });
  isAuthMailLoading.value = false;
  if (status !== 204) {
    return;
  }
  if (isResent) {
    messagingStore.addNotification({
      iconColor: 'positive',
      icon: '$icon_check_zero',
      title: t(`Resent authentication code`),
      body: `${t('An authentication code was sent to your email')} ${data?.email}`,
    });
  } else {
    moveToNextStep();
  }
};

const data = reactive({
  password: '',
  email: '',
  code: '',
});
</script>

<style lang="scss" scoped>
@import '../../styles/public/main.scss';

.edit-email {
  width: 50%;
  margin: auto;
  &__verify-password {
    margin-bottom: 56px;
  }
  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 32px;
    &__title {
      @include subtitle-1;
    }
    &__subtitle {
      @include body-2;
      color: $secondary;
      max-width: 480px;
    }
  }
}
.progress-steps {
  display: flex;
  &__buttons {
    position: relative;
    top: -63px;
    text-align: end;
  }
  &__next-button {
    margin-left: 12px;
  }
}

@media screen and (max-width: 768px) {
  .edit-email {
    width: 90%;
  }
}
</style>
